import { Check } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import type { PaymentDetails, CustomerPermit, SiteBooking } from "@/types/payment";
import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { PermitDetailsSection } from "./components/PermitDetailsSection";
import { BookingDetailsSection } from "./components/BookingDetailsSection";
import { ProcessingNotice } from "./components/ProcessingNotice";
import { TransactionDetailsSection } from "./components/TransactionDetailsSection";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useSiteAvailability } from "@/contexts/SiteAvailabilityContext";
import { useCart } from "@/contexts/CartContext";
import { dateToString } from "@/utils/formatters";

interface PaymentSuccessProps {
  paymentDetails: PaymentDetails | null;
  permitEmails?: Array<{id: string, email: boolean}>;
  onResendEmail?: (permitId: string) => Promise<void>;
  recoveredBookingData?: any;
  onReturnHome?: () => void;
}

export const PaymentSuccess = ({ 
  paymentDetails, 
  permitEmails = [],
  onResendEmail,
  recoveredBookingData,
  onReturnHome
}: PaymentSuccessProps) => {
  const { toast } = useToast();
  const { notifyBookingCompleted } = useSiteAvailability();
  const { clearCart } = useCart();
  const [permitDetails, setPermitDetails] = useState<CustomerPermit[]>([]);
  const [bookingDetails, setBookingDetails] = useState<SiteBooking[]>([]);
  const [loading, setLoading] = useState(false);
  const [resendingEmail, setResendingEmail] = useState<string | null>(null);
  const retryCountRef = useRef(0);
  const maxRetries = 5;
  const [retryCountDisplay, setRetryCountDisplay] = useState(0);
  const hasLoggedBookingQueryInfo = useRef(false);
  const processedBookingKeys = useRef<Set<string>>(new Set());
  const [emailSending, setEmailSending] = useState(false);

  // If we have recovered booking data but no webhook process, create synthetic booking record
  const hasRecoveredData = Boolean(recoveredBookingData && 
    recoveredBookingData.siteId && 
    recoveredBookingData.startDate && 
    recoveredBookingData.endDate);

  // Cart clearing is handled at PaymentSuccessPage level — no duplicate clear here
  // to avoid an extra re-render flash on the success view.

  const handleReturnHome = () => {
    // Clear cart again as a failsafe before navigating
    clearCart();
    
    if (onReturnHome) {
      onReturnHome();
    } else {
      // Fallback navigation if no custom handler provided
      window.location.href = '/';
    }
  };

  const fetchData = useCallback(async () => {
    if (!paymentDetails) return;
    
    setLoading(true);
    
    try {
      // Fetch permit details if we have permit IDs
      if (paymentDetails.createdPermitIds && paymentDetails.createdPermitIds.length > 0) {
        console.log('Fetching permit details for IDs:', paymentDetails.createdPermitIds);
        
        const { data: permits, error: permitsError } = await supabase
          .from('customer_permits')
          .select('*')
          .in('permit_id', paymentDetails.createdPermitIds);
        
        if (permitsError) {
          console.error('Error fetching permits:', permitsError);
        } else {
          console.log('Fetched permits:', permits);
          // Transform the data to match our TypeScript interface
          const permitsWithItemType: CustomerPermit[] = (permits || []).map(permit => ({
            ...permit,
            itemType: 'fishing' as const,
            // Safely cast metadata to the expected type
            metadata: (permit.metadata && typeof permit.metadata === 'object' && !Array.isArray(permit.metadata)) 
              ? permit.metadata as CustomerPermit['metadata']
              : null
          }));
          setPermitDetails(permitsWithItemType);
        }
      }
      
      // Fetch booking details if we have booking IDs
      if (paymentDetails.createdBookingIds && paymentDetails.createdBookingIds.length > 0) {
        console.log('Fetching booking details for IDs:', paymentDetails.createdBookingIds);
        
        // Enhanced logging for booking queries
        if (!hasLoggedBookingQueryInfo.current) {
          console.log('Booking query info:', {
            bookingIds: paymentDetails.createdBookingIds,
            queryType: 'site_bookings table',
            filterField: 'booking_id'
          });
          hasLoggedBookingQueryInfo.current = true;
        }
        
        const { data: bookings, error: bookingsError } = await supabase
          .from('site_bookings')
          .select(`
            *,
            site_details:site_id (
              site_number,
              location,
              site_type,
              price_per_night
            )
          `)
          .in('booking_id', paymentDetails.createdBookingIds);
        
        if (bookingsError) {
          console.error('Error fetching bookings:', bookingsError);
        } else {
          console.log('Fetched bookings:', bookings);
          // Transform the data to match our TypeScript interface
          const bookingsWithItemType: SiteBooking[] = (bookings || []).map(booking => ({
            ...booking,
            itemType: 'rv-site' as const
          }));
          setBookingDetails(bookingsWithItemType);
          
          // Notify site availability context about completed bookings
          if (bookings && bookings.length > 0) {
            bookings.forEach(booking => {
              const key = `${paymentDetails.id}::${booking.booking_id}`;
              if (!processedBookingKeys.current.has(key)) {
                console.log('Notifying booking completion for:', booking.booking_id);
                notifyBookingCompleted(
                  booking.site_id, 
                  dateToString(new Date(booking.start_date)), 
                  dateToString(new Date(booking.end_date))
                );
                processedBookingKeys.current.add(key);
              }
            });
          }
        }
      }
      
      // Handle recovered booking data
      if (hasRecoveredData && bookingDetails.length === 0) {
        console.log('Creating synthetic booking from recovered data:', recoveredBookingData);
        
        const syntheticBooking: SiteBooking = {
          id: 'recovered',
          booking_id: 'RECOVERED',
          site_id: recoveredBookingData.siteId,
          customer_name: recoveredBookingData.customerName || 'Unknown',
          customer_email: recoveredBookingData.customerEmail || '',
          start_date: recoveredBookingData.startDate,
          end_date: recoveredBookingData.endDate,
          total_price: recoveredBookingData.totalPrice || 0,
          payment_status: 'paid',
          payment_id: paymentDetails.id,
          created_at: new Date().toISOString(),
          email_sent: false,
          email_sent_at: null,
          itemType: 'rv-site' as const,
          site_details: {
            site_number: 'Unknown',
            location: 'Unknown',
            site_type: 'standard',
            price_per_night: recoveredBookingData.totalPrice || 0
          }
        };
        
        setBookingDetails([syntheticBooking]);
        
        const recoveredKey = `${paymentDetails.id}::RECOVERED`;
        if (!processedBookingKeys.current.has(recoveredKey)) {
          notifyBookingCompleted(
            recoveredBookingData.siteId,
            dateToString(new Date(recoveredBookingData.startDate)),
            dateToString(new Date(recoveredBookingData.endDate))
          );
          processedBookingKeys.current.add(recoveredKey);
        }
      }
      
    } catch (error) {
      console.error('Error fetching data:', error);
      
      if (retryCountRef.current < maxRetries) {
        const next = retryCountRef.current + 1;
        console.log(`Retrying data fetch (attempt ${next}/${maxRetries})`);
        retryCountRef.current = next;
        setRetryCountDisplay(next);
        setTimeout(() => fetchData(), 2000 * next);
      } else {
        toast({
          title: "Error",
          description: "Unable to load booking details. Please refresh the page.",
          variant: "destructive"
        });
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentDetails?.id, hasRecoveredData, recoveredBookingData, toast, notifyBookingCompleted]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentDetails?.id]);

  const handleResendEmail = async (itemId: string) => {
    if (!onResendEmail) return;
    
    // Enhanced validation
    if (!itemId || itemId === 'Pending' || itemId === 'undefined' || itemId === 'null' || itemId === 'RECOVERED') {
      console.error('Cannot resend email for invalid ID:', itemId);
      toast({
        title: "Invalid Request",
        description: "Cannot resend email for this item.",
        variant: "destructive"
      });
      return;
    }
    
    setResendingEmail(itemId);
    setEmailSending(true);
    
    try {
      console.log('Resending email for:', itemId);
      await onResendEmail(itemId);
    } catch (error) {
      console.error('Error resending email:', error);
    } finally {
      setResendingEmail(null);
      setEmailSending(false);
    }
  };

  const getEmailStatus = (itemId: string) => {
    if (itemId === 'RECOVERED' || itemId === 'Pending') return false;
    return permitEmails.find(email => email.id === itemId)?.email || false;
  };

  if (!paymentDetails) {
    return (
      <div className="text-center py-8">
        <ProcessingNotice 
          paymentDetails={{
            permitsCreated: false,
            bookingsCreated: false,
            paymentStatus: 'processing'
          }}
        />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div className="text-center">
        <div className="flex justify-center mb-4">
          <div className="bg-green-100 p-3 rounded-full">
            <Check className="h-8 w-8 text-green-600" />
          </div>
        </div>
        <h1 className="text-3xl font-bold text-green-600 mb-2">Payment Successful!</h1>
        <p className="text-gray-600 mb-4">
          Your transaction has been completed successfully and your cart has been cleared.
        </p>
        <p className="text-sm text-gray-500">
          Email confirmations are being sent automatically. You can resend them if needed.
        </p>
      </div>

      <TransactionDetailsSection paymentDetails={paymentDetails} />

      {loading && (
        <ProcessingNotice 
          paymentDetails={{
            permitsCreated: false,
            bookingsCreated: false,
            paymentStatus: 'processing'
          }}
        />
      )}

      {permitDetails.length > 0 && (
        <PermitDetailsSection 
          permitDetails={permitDetails}
          bookingDetails={bookingDetails}
          loading={loading}
          showRefreshButton={false}
          handleRefresh={fetchData}
          retryCount={retryCountDisplay}
          maxRetries={maxRetries}
          paymentDetails={paymentDetails}
          permitEmails={permitEmails}
          resendingEmail={resendingEmail}
          onResendEmail={handleResendEmail}
        />
      )}

      {bookingDetails.length > 0 && (
        <BookingDetailsSection 
          paymentDetails={paymentDetails}
          bookingDetails={bookingDetails}
          loading={loading}
          permitEmails={permitEmails}
          resendingEmail={resendingEmail}
          onResendEmail={handleResendEmail}
          recoveredBookingData={recoveredBookingData}
        />
      )}

      {permitDetails.length === 0 && bookingDetails.length === 0 && !loading && (
        <div className="text-center py-8">
          <p className="text-gray-600">
            Your purchase details are being processed. You'll receive email confirmations shortly.
          </p>
          <Button 
            onClick={fetchData}
            className="mt-4"
            disabled={loading}
          >
            Refresh Details
          </Button>
        </div>
      )}

      <div className="text-center pt-6 border-t">
        <Button onClick={handleReturnHome}>Return to Home</Button>
      </div>
    </div>
  );
};
